/**
 * Document Classification Service
 * Detects document type based on extracted text and metadata
 */

export type DocumentType = 'DEED' | 'MORTGAGE_RELEASE' | 'PLAT' | 'UNKNOWN';

export interface ClassificationResult {
  documentType: DocumentType;
  confidence: number;
  matchedKeywords: string[];
  method: 'keyword' | 'ml' | 'manual';
}

class DocumentClassificationService {
  // Keyword patterns for each document type
  private readonly patterns: Record<DocumentType, string[]> = {
    DEED: [
      'WARRANTY DEED',
      'QUITCLAIM DEED',
      'SPECIAL WARRANTY DEED',
      'GIFT DEED',
      'TRANSFER ON DEATH DEED',
      'DEED OF TRUST',
      'GRANT DEED',
      'BARGAIN AND SALE DEED',
      'GRANTOR',
      'GRANTEE',
      'HEREBY GRANTS',
      'CONVEYS AND WARRANTS',
      'THIS DEED'
    ],
    MORTGAGE_RELEASE: [
      'MORTGAGE RELEASE',
      'SATISFACTION OF MORTGAGE',
      'RELEASE OF DEED OF TRUST',
      'SATISFACTION OF DEED OF TRUST',
      'DISCHARGE OF MORTGAGE',
      'LIEN RELEASE',
      'PAID IN FULL',
      'HEREBY RELEASES',
      'MORTGAGE IS SATISFIED'
    ],
    PLAT: [
      'PLAT',
      'SUBDIVISION',
      'SURVEY',
      'LOT',
      'BLOCK',
      'SECTION',
      'TOWNSHIP',
      'RANGE',
      'SURVEYOR',
      'BOUNDARY',
      'PARCEL MAP'
    ],
    UNKNOWN: []
  };

  /**
   * Classify document based on extracted text
   */
  classifyDocument(extractedText: string): ClassificationResult {
    const upperText = extractedText.toUpperCase();
    const scores: Record<DocumentType, { score: number; keywords: string[] }> = {
      DEED: { score: 0, keywords: [] },
      MORTGAGE_RELEASE: { score: 0, keywords: [] },
      PLAT: { score: 0, keywords: [] },
      UNKNOWN: { score: 0, keywords: [] }
    };

    // Check each document type's keywords
    Object.entries(this.patterns).forEach(([docType, keywords]) => {
      if (docType === 'UNKNOWN') return;

      keywords.forEach(keyword => {
        if (upperText.includes(keyword)) {
          scores[docType as DocumentType].score += 1;
          scores[docType as DocumentType].keywords.push(keyword);
        }
      });
    });

    // Find document type with highest score
    let bestType: DocumentType = 'UNKNOWN';
    let bestScore = 0;
    let matchedKeywords: string[] = [];

    Object.entries(scores).forEach(([docType, data]) => {
      if (data.score > bestScore) {
        bestScore = data.score;
        bestType = docType as DocumentType;
        matchedKeywords = data.keywords;
      }
    });

    // Calculate confidence (0-1)
    // More matched keywords = higher confidence
    const confidence = bestScore > 0 ? Math.min(bestScore / 3, 1.0) : 0;

    // If confidence is too low, mark as UNKNOWN
    if (confidence < 0.3) {
      bestType = 'UNKNOWN';
    }

    console.log('Document classification:', {
      documentType: bestType,
      confidence,
      matchedKeywords,
      scores
    });

    return {
      documentType: bestType,
      confidence,
      matchedKeywords,
      method: 'keyword'
    };
  }

  /**
   * Classify from extracted data object
   */
  classifyFromExtractedData(extractedData: any): ClassificationResult {
    // Try to get text from various sources
    let text = '';

    // Option 1: From markdown content
    if (extractedData.content?.markdown) {
      text = extractedData.content.markdown;
    }
    // Option 2: From fields
    else if (extractedData.fields) {
      text = extractedData.fields
        .map((f: any) => `${f.name}: ${f.value}`)
        .join(' ');
    }
    // Option 3: From raw text
    else if (extractedData.text) {
      text = extractedData.text;
    }

    // Check if document type already in metadata
    if (extractedData.metadata?.documentType) {
      const existingType = extractedData.metadata.documentType.toUpperCase();
      if (['DEED', 'MORTGAGE_RELEASE', 'PLAT'].includes(existingType)) {
        return {
          documentType: existingType as DocumentType,
          confidence: 1.0,
          matchedKeywords: [],
          method: 'manual'
        };
      }
    }

    return this.classifyDocument(text);
  }

  /**
   * Get display name for document type
   */
  getDisplayName(documentType: DocumentType): string {
    const names: Record<DocumentType, string> = {
      DEED: 'Property Deed',
      MORTGAGE_RELEASE: 'Mortgage Release',
      PLAT: 'Plat/Survey',
      UNKNOWN: 'Unknown Document'
    };
    return names[documentType];
  }

  /**
   * Check if document type is supported
   */
  isSupported(documentType: DocumentType): boolean {
    return documentType !== 'UNKNOWN';
  }
}

// Export singleton instance
const documentClassificationService = new DocumentClassificationService();
export default documentClassificationService;
